/** Layout tax on prose ceiling — tuned so short headings/lists don’t consume most of a 240-word page. */
export const AUTHOR_WORD_COST_H1 = 30;
export const AUTHOR_WORD_COST_H2 = 18;
export const AUTHOR_WORD_COST_H3 = 15;
/** Each table row, including header row */
export const AUTHOR_WORD_COST_TABLE_ROW = 18;
/** Each bullet or numbered list item */
export const AUTHOR_WORD_COST_LIST_ITEM = 12;
/** Above this prose count (typed words), table / code block / page images are disallowed */
export const AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS = 120;

function countWordsInText(text) {
  if (!text || !String(text).trim()) return 0;
  return String(text).trim().split(/\s+/).filter(Boolean).length;
}

export function countHeadings(doc) {
  let h1 = 0;
  let h2 = 0;
  let h3 = 0;
  doc.descendants((node) => {
    if (node.type.name !== 'heading') return;
    const lv = Number(node.attrs?.level);
    if (lv === 1) h1 += 1;
    else if (lv === 2) h2 += 1;
    else if (lv === 3) h3 += 1;
  });
  return { h1, h2, h3 };
}

export function countTableRows(doc) {
  let rows = 0;
  doc.descendants((node) => {
    if (node.type.name === 'tableRow') rows += 1;
  });
  return rows;
}

export function countListItems(doc) {
  let n = 0;
  doc.descendants((node) => {
    if (node.type.name === 'listItem') n += 1;
  });
  return n;
}

export function totalCodeBlockWords(doc) {
  let total = 0;
  doc.descendants((node) => {
    if (node.type.name === 'codeBlock') {
      total += countWordsInText(node.textContent);
    }
  });
  return total;
}

export function docHasTable(doc) {
  let found = false;
  doc.descendants((node) => {
    if (node.type.name === 'table') found = true;
  });
  return found;
}

export function docHasCodeBlock(doc) {
  let found = false;
  doc.descendants((node) => {
    if (node.type.name === 'codeBlock') found = true;
  });
  return found;
}

export function computeStructuralWordTax(doc) {
  const { h1, h2, h3 } = countHeadings(doc);
  const rows = countTableRows(doc);
  const listItems = countListItems(doc);
  return (
    h1 * AUTHOR_WORD_COST_H1 +
    h2 * AUTHOR_WORD_COST_H2 +
    h3 * AUTHOR_WORD_COST_H3 +
    rows * AUTHOR_WORD_COST_TABLE_ROW +
    listItems * AUTHOR_WORD_COST_LIST_ITEM
  );
}

/**
 * Table / code / page images are pairwise exclusive; word count gates rich embeds.
 * When `applyWordCountGate` is false (image-only layouts), stale body text must not block replacing images.
 */
export function computeComposeEmbedGuards({
  pageWordCount,
  hasPageImages,
  doc,
  applyWordCountGate = true,
}) {
  const safeDoc = doc;
  const hasTable = safeDoc ? docHasTable(safeDoc) : false;
  const hasCode = safeDoc ? docHasCodeBlock(safeDoc) : false;
  const wcOverRich =
    applyWordCountGate &&
    typeof pageWordCount === 'number' &&
    pageWordCount > AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS;

  return {
    blockTable: wcOverRich || Boolean(hasPageImages) || hasCode,
    blockCodeBlock: wcOverRich || Boolean(hasPageImages) || hasTable,
    /** Upload / change page visuals (server images) — blocked when word count is over 120 */
    blockPageVisuals: wcOverRich || hasTable || hasCode,
    /**
     * Full-bleed uploads only when the editor has zero words. Any text ⇒ half-page slots only
     * (until word count exceeds 120, when all page images are blocked via `blockPageVisuals`).
     */
    blockFullPageVisuals:
      applyWordCountGate &&
      typeof pageWordCount === 'number' &&
      pageWordCount > 0,
  };
}

/**
 * True if inserting one more row would shrink the prose ceiling below current prose usage.
 * `proseWordCount` must match `countProseWordsFromDoc` (excluding code blocks).
 */
export function wouldNextTableRowReject(baseMaxWords, doc, proseWordCount) {
  if (!doc || typeof baseMaxWords !== 'number' || baseMaxWords <= 0) return false;
  const words = typeof proseWordCount === 'number' ? proseWordCount : 0;
  const ceil = computeEffectiveWordCeiling(baseMaxWords, doc);
  return words > Math.max(0, ceil - AUTHOR_WORD_COST_TABLE_ROW);
}

/**
 * Ceiling for prose (body words). Base comes from layout (240 / 120 / …).
 * Structural headings, table rows, list items, and all code-block words reduce the allowance.
 */
export function computeEffectiveWordCeiling(baseMax, doc) {
  if (!doc || typeof baseMax !== 'number' || baseMax <= 0) return 0;
  const tax = computeStructuralWordTax(doc);
  const codeWords = totalCodeBlockWords(doc);
  return Math.max(0, baseMax - tax - codeWords);
}
